import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'

const { 
  FiCalendar, FiUser, FiGift, FiStar, FiClock, FiFilter,
  FiChevronDown, FiChevronUp, FiTrendingUp, FiAward,
  FiRefreshCw, FiDownload, FiSearch
} = FiIcons

const RewardHistory = () => {
  const { redemptions, rewards } = useFamily()
  const { family } = useAuth()
  const [expandedRedemption, setExpandedRedemption] = useState(null)
  const [sortBy, setSortBy] = useState('date') // date, child, reward, points
  const [sortOrder, setSortOrder] = useState('desc') // asc, desc
  const [filterChild, setFilterChild] = useState('all')
  const [filterReward, setFilterReward] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useState('all') // all, week, month, quarter, year

  // Filter and sort redemptions
  const filteredAndSortedRedemptions = useMemo(() => {
    let filtered = [...redemptions]

    // Apply date filter
    if (dateRange !== 'all') {
      const now = new Date()
      let startDate = new Date()

      switch (dateRange) {
        case 'week':
          startDate.setDate(now.getDate() - 7)
          break
        case 'month':
          startDate.setMonth(now.getMonth() - 1)
          break
        case 'quarter':
          startDate.setMonth(now.getMonth() - 3)
          break
        case 'year':
          startDate.setFullYear(now.getFullYear() - 1)
          break
      }

      filtered = filtered.filter(redemption => 
        new Date(redemption.redeemed_at) >= startDate
      )
    }

    // Apply child filter
    if (filterChild !== 'all') {
      filtered = filtered.filter(redemption => redemption.child_id === filterChild)
    }

    // Apply reward filter
    if (filterReward !== 'all') {
      filtered = filtered.filter(redemption => redemption.reward_id === filterReward)
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(redemption => {
        const reward = rewards.find(r => r.id === redemption.reward_id)
        const child = family?.children?.find(c => c.id === redemption.child_id)
        
        return (
          reward?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          child?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          redemption.points_spent?.toString().includes(searchTerm)
        )
      })
    }

    // Sort redemptions
    filtered.sort((a, b) => {
      let aValue, bValue

      switch (sortBy) {
        case 'date':
          aValue = new Date(a.redeemed_at)
          bValue = new Date(b.redeemed_at)
          break
        case 'child':
          const aChild = family?.children?.find(c => c.id === a.child_id)
          const bChild = family?.children?.find(c => c.id === b.child_id)
          aValue = aChild?.name || ''
          bValue = bChild?.name || ''
          break
        case 'reward':
          const aReward = rewards.find(r => r.id === a.reward_id)
          const bReward = rewards.find(r => r.id === b.reward_id)
          aValue = aReward?.title || ''
          bValue = bReward?.title || ''
          break
        case 'points':
          aValue = a.points_spent || 0
          bValue = b.points_spent || 0
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [redemptions, rewards, family, sortBy, sortOrder, filterChild, filterReward, searchTerm, dateRange])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const totalRedemptions = filteredAndSortedRedemptions.length
    const totalPointsSpent = filteredAndSortedRedemptions.reduce((sum, r) => sum + (r.points_spent || 0), 0)
    const uniqueChildren = new Set(filteredAndSortedRedemptions.map(r => r.child_id)).size
    const uniqueRewards = new Set(filteredAndSortedRedemptions.map(r => r.reward_id)).size

    return { totalRedemptions, totalPointsSpent, uniqueChildren, uniqueRewards }
  }, [filteredAndSortedRedemptions])

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const exportData = () => {
    const csvData = filteredAndSortedRedemptions.map(redemption => {
      const reward = rewards.find(r => r.id === redemption.reward_id)
      const child = family?.children?.find(c => c.id === redemption.child_id)
      
      return {
        Date: new Date(redemption.redeemed_at).toLocaleDateString(),
        Time: new Date(redemption.redeemed_at).toLocaleTimeString(),
        Child: child?.name || 'Unknown',
        Reward: reward?.title || 'Unknown',
        'Points Spent': redemption.points_spent || 0,
        Status: redemption.status || 'completed'
      }
    })

    const csvContent = [
      Object.keys(csvData[0] || {}).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reward-history-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reward History</h2>
          <p className="text-gray-600 dark:text-gray-400">Complete history of all reward redemptions</p>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={exportData}
            disabled={filteredAndSortedRedemptions.length === 0}
          >
            <SafeIcon icon={FiDownload} />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {summaryStats.totalRedemptions}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Redemptions</p>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {summaryStats.totalPointsSpent.toLocaleString()}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Points Spent</p>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
              {summaryStats.uniqueChildren}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Active Children</p>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {summaryStats.uniqueRewards}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Different Rewards</p>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiFilter} className="text-gray-500 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters & Search</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date Range
            </label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Time</option>
              <option value="week">Last Week</option>
              <option value="month">Last Month</option>
              <option value="quarter">Last 3 Months</option>
              <option value="year">Last Year</option>
            </select>
          </div>

          {/* Child Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Child
            </label>
            <select
              value={filterChild}
              onChange={(e) => setFilterChild(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Children</option>
              {family?.children?.map(child => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </select>
          </div>

          {/* Reward Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reward
            </label>
            <select
              value={filterReward}
              onChange={(e) => setFilterReward(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Rewards</option>
              {rewards.map(reward => (
                <option key={reward.id} value={reward.id}>
                  {reward.title}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sort By
            </label>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-')
                setSortBy(field)
                setSortOrder(order)
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="date-desc">Newest First</option>
              <option value="date-asc">Oldest First</option>
              <option value="child-asc">Child A-Z</option>
              <option value="child-desc">Child Z-A</option>
              <option value="reward-asc">Reward A-Z</option>
              <option value="reward-desc">Reward Z-A</option>
              <option value="points-desc">Most Points</option>
              <option value="points-asc">Least Points</option>
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search by child name, reward title, or points..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
      </Card>

      {/* History Table */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Redemption History ({filteredAndSortedRedemptions.length} records)
          </h3>
        </div>

        {filteredAndSortedRedemptions.length > 0 ? (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <AnimatePresence>
              {filteredAndSortedRedemptions.map((redemption, index) => {
                const reward = rewards.find(r => r.id === redemption.reward_id)
                const child = family?.children?.find(c => c.id === redemption.child_id)
                const dateTime = formatDate(redemption.redeemed_at)
                const isExpanded = expandedRedemption === redemption.id

                return (
                  <motion.div
                    key={redemption.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div 
                      className="p-6 cursor-pointer"
                      onClick={() => setExpandedRedemption(isExpanded ? null : redemption.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl">{reward?.emoji || '🎁'}</div>
                          
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                                {reward?.title || 'Unknown Reward'}
                              </h4>
                              <Badge variant="primary">
                                <SafeIcon icon={FiStar} className="text-xs" />
                                {redemption.points_spent} pts
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <SafeIcon icon={FiUser} className="text-xs" />
                                <span>{child?.name || 'Unknown Child'}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <SafeIcon icon={FiCalendar} className="text-xs" />
                                <span>{dateTime.date}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <SafeIcon icon={FiClock} className="text-xs" />
                                <span>{dateTime.time}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Badge 
                            variant={redemption.status === 'completed' ? 'success' : 'warning'}
                          >
                            {redemption.status || 'completed'}
                          </Badge>
                          
                          <SafeIcon 
                            icon={isExpanded ? FiChevronUp : FiChevronDown} 
                            className="text-gray-400 dark:text-gray-500"
                          />
                        </div>
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-6 pb-6 border-t border-gray-100 dark:border-gray-700"
                        >
                          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                                Reward Details
                              </h5>
                              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <p><strong>Description:</strong> {reward?.description || 'No description'}</p>
                                <p><strong>Points Required:</strong> {reward?.points_required || 'Unknown'}</p>
                                <p><strong>Category:</strong> {reward?.category || 'Uncategorized'}</p>
                              </div>
                            </div>
                            
                            <div>
                              <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                                Redemption Info
                              </h5>
                              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <p><strong>Child Age:</strong> {child?.age || 'Unknown'}</p>
                                <p><strong>Redeemed At:</strong> {new Date(redemption.redeemed_at).toLocaleString()}</p>
                                <p><strong>Status:</strong> {redemption.status || 'Completed'}</p>
                                {redemption.notes && (
                                  <p><strong>Notes:</strong> {redemption.notes}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="text-6xl mb-4">📋</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No redemptions found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {redemptions.length === 0 
                ? "No rewards have been redeemed yet."
                : "No redemptions match your current filters."
              }
            </p>
            {redemptions.length > 0 && (
              <Button
                variant="outline"
                onClick={() => {
                  setDateRange('all')
                  setFilterChild('all')
                  setFilterReward('all')
                  setSearchTerm('')
                }}
              >
                <SafeIcon icon={FiRefreshCw} />
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  )
}

export default RewardHistory